import React, { useState, useEffect, useRef } from "react";
import {
  MagnifyingGlassIcon,
  GlobeAltIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { OnlineShop } from "../../services/api";
import apiService from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";

interface OnlineShopSearchProps {
  onSelect: (shop: OnlineShop) => void;
  selectedShop?: OnlineShop | null;
}

const OnlineShopSearch: React.FC<OnlineShopSearchProps> = ({
  onSelect,
  selectedShop: initialShop,
}) => {
  const [searchText, setSearchText] = useState("");
  const [shops, setShops] = useState<OnlineShop[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState<OnlineShop | null>(
    initialShop || null,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSearchText = useDebounce(searchText, 300);

  // Update when external selectedShop changes
  useEffect(() => {
    if (initialShop) {
      setSelectedShop(initialShop);
      setSearchText(initialShop.storeName);
    }
  }, [initialShop]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchShops = async () => {
      // Only search if user has typed at least 2 characters
      if (!debouncedSearchText || debouncedSearchText.length < 2) {
        setShops([]);
        setIsOpen(false);
        return;
      }

      setLoading(true);
      try {
        const results = await apiService.searchOnlineShops(debouncedSearchText);
        // Filter by search text client-side if API doesn't support search param
        let filteredResults = results || [];
        const searchLower = debouncedSearchText.toLowerCase();
        filteredResults = filteredResults.filter((shop) =>
          shop.storeName.toLowerCase().includes(searchLower),
        );
        setShops(filteredResults);
        setIsOpen(true);
      } catch (error: any) {
        console.error("Online shop search error:");
        setShops([]);
      } finally {
        setLoading(false);
      }
    };

    searchShops();
  }, [debouncedSearchText]);

  const handleSelect = (shop: OnlineShop) => {
    setSelectedShop(shop);
    setSearchText(shop.storeName);
    setIsOpen(false);
    onSelect(shop);
  };

  const handleClear = () => {
    setSearchText("");
    setSelectedShop(null);
    setShops([]);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Search Online Shop *
      </label>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => shops.length > 0 && setIsOpen(true)}
          placeholder="Search online shops by name..."
          className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
        />
        {searchText && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {loading && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
              Loading online shops...
            </span>
          </div>
        </div>
      )}

      {!loading && isOpen && shops.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {shops.map((shop) => (
            <button
              key={shop.storeId}
              type="button"
              onClick={() => handleSelect(shop)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors ${
                selectedShop?.storeId === shop.storeId
                  ? "bg-primary-50 dark:bg-primary-900/20"
                  : ""
              }`}
            >
              <div className="flex items-start space-x-3">
                {shop.logoImage ? (
                  <img
                    src={shop.logoImage}
                    alt={shop.storeName}
                    className="h-8 w-8 rounded object-contain bg-white flex-shrink-0"
                  />
                ) : (
                  <GlobeAltIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {shop.storeName}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {shop.cashbackRate && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        {shop.cashbackRate}% cashback
                      </span>
                    )}
                    {shop.countries && shop.countries.length > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                        {shop.countries.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading &&
        isOpen &&
        searchText &&
        shops.length === 0 &&
        debouncedSearchText &&
        debouncedSearchText.length >= 2 && (
          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              No online shops found for "{searchText}"
            </p>
          </div>
        )}

      {selectedShop && (
        <div className="mt-2 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              {selectedShop.logoImage ? (
                <img
                  src={selectedShop.logoImage}
                  alt={selectedShop.storeName}
                  className="h-10 w-10 rounded object-contain bg-white"
                />
              ) : (
                <GlobeAltIcon className="h-10 w-10 text-purple-400" />
              )}
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedShop.storeName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Store ID: {selectedShop.storeId}
                </p>
                {selectedShop.cashbackRate && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    {selectedShop.cashbackRate}% cashback
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Search affiliate partner online shops
      </p>
    </div>
  );
};

export default OnlineShopSearch;
